import { useEffect, useState } from "react";
import { RefreshCw, X } from "lucide-react";
import { useRegisterSW } from "virtual:pwa-register/react";
import { applyWhenIdle, canUpdateSilently, isEmbeddedWebview, isOutdated, reloadOnce } from "../utils/appUpdate";

/**
 * Плашка «Вышла новая версия — Обновить» (#444).
 * Веб (PWA): новая версия ставится service worker'ом в ожидание; в первые секунды после
 * запуска включаем её сами (работы ещё нет), позже — только по кнопке: брокер может быть
 * посреди заведения заявки.
 * Встроенные браузеры (Telegram, MAX): без service worker'а (utils/appUpdate.ts) — сверяем
 * версию с сервером при запуске, возврате в приложение и по таймеру.
 */
const CHECK_EVERY_MS = 15 * 60 * 1000;

export default function UpdateBanner() {
  return isEmbeddedWebview() ? <WebviewUpdate /> : <PwaUpdate />;
}

function PwaUpdate() {
  const { needRefresh: [needRefresh, setNeedRefresh], updateServiceWorker } = useRegisterSW({
    onRegisteredSW(_url, reg) {
      if (!reg) return;
      const check = async () => {
        reg.update().catch(() => { /* офлайн — проверим позже */ });
      };
      check();
      setInterval(check, CHECK_EVERY_MS);
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") check();
      });
    },
  });

  const apply = () => {
    void updateServiceWorker(true);
    // Вкладка, которой service worker ещё не управлял (первый визит), не получит
    // события смены контроллера — перезагружаем сами, если это не сделал workbox.
    setTimeout(() => window.location.reload(), 1500);
  };

  // Новая версия нашлась сразу после запуска — включаем без спроса. Если брокер уже за
  // работой, ждём момента, когда обновление ничего не прервёт: вкладка ушла в фон или
  // прошёл час. «Позже» откладывает плашку, но не отменяет само обновление — иначе
  // закрывшие её остаются на старой версии неделями.
  useEffect(() => {
    if (!needRefresh) return;
    if (canUpdateSilently()) { apply(); return; }
    return applyWhenIdle(apply);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needRefresh]);

  return needRefresh ? <Banner onUpdate={apply} onLater={() => setNeedRefresh(false)} /> : null;
}

function WebviewUpdate() {
  const [outdated, setOutdated] = useState(false);
  useEffect(() => {
    let alive = true;
    const check = async () => {
      if (await isOutdated() && alive) {
        if (canUpdateSilently()) reloadOnce("встроенный браузер: на сервере новая версия");
        else { setOutdated(true); applyWhenIdle(() => window.location.reload()); }
      }
    };
    void check();
    const id = setInterval(check, CHECK_EVERY_MS);
    // Мессенджер держит webview в фоне: вернулись в приложение — сверяемся.
    const onVisible = () => { if (document.visibilityState === "visible") void check(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => { alive = false; clearInterval(id); document.removeEventListener("visibilitychange", onVisible); };
  }, []);
  return outdated ? <Banner onUpdate={() => window.location.reload()} onLater={() => setOutdated(false)} /> : null;
}

function Banner({ onUpdate, onLater }: { onUpdate: () => void; onLater: () => void }) {
  // Заголовок вкладки подсказывает, даже если плашку не видно (свёрнутое окно чата и т.п.).
  useEffect(() => {
    const t = document.title;
    document.title = `● ${t}`;
    return () => { document.title = t; };
  }, []);
  return (
    <div role="status"
      className="app-floating fixed left-1/2 -translate-x-1/2 bottom-[calc(1rem+env(safe-area-inset-bottom))] z-(--z-sheet) w-[min(520px,calc(100vw-1.5rem))] flex items-center gap-3 bg-slate-900 text-white rounded-2xl shadow-2xl px-4 py-3">
      <RefreshCw className="w-4 h-4 shrink-0 text-indigo-300" />
      <div className="flex-1 min-w-0 text-sm">
        <b>Вышла новая версия ANYskin.</b>
        <span className="text-slate-300"> Обновите, чтобы получить изменения.</span>
      </div>
      <button onClick={onUpdate}
        className="shrink-0 min-h-11 md:min-h-0 px-3 py-1.5 bg-indigo-500 hover:bg-indigo-400 text-white text-xs font-bold rounded-lg cursor-pointer">
        Обновить
      </button>
      <button onClick={onLater} title="Позже" aria-label="Напомнить позже"
        className="shrink-0 grid place-items-center w-11 h-11 md:w-auto md:h-auto -mr-2 md:mr-0 p-1 text-slate-400 hover:text-white cursor-pointer"><X className="w-4 h-4" /></button>
    </div>
  );
}
